import { lazy, Suspense, type FC } from 'react';
import { useLocation } from 'react-router';
import { Button } from 'src/components/buttons-and-actions/button';
import { DropdownTrigger } from 'src/components/buttons-and-actions/dropdown-trigger';
import type { LoadedAppShellData } from 'src/components/layout/app-shell/use-app-shell-data';
import { UserNav } from 'src/components/layout/app-shell/user-nav';
import {
  BugIcon,
  EllipsisVerticalIcon,
  IdCardIcon,
  SettingsIcon,
  UserIcon,
} from 'src/components/media-and-icons/icons';
import { Link } from 'src/components/navigation/link';
import { Tooltip } from 'src/components/overlay/tooltip';
import { useDeveloperTools } from 'src/pages/developer-tools/use-developer-tools';
import { cn } from 'src/utils/cn';

const DeveloperTools = lazy(() => import('src/pages/developer-tools/icon'));

export type UserControlsProps = {
  appShellData: LoadedAppShellData;
  isSidebarExpanded: boolean;
  isSidebarTransitioning: boolean;
};

export const UserControls: FC<UserControlsProps> = ({
  appShellData,
  isSidebarExpanded,
  isSidebarTransitioning,
}) => {
  const { pathname, search } = useLocation();
  const developerTools = useDeveloperTools();
  const showTooltip = !isSidebarExpanded && !isSidebarTransitioning;

  return (
    <div
      className={cn(
        `
          shrink border-t border-border-sidebar-minimal px-(--sidebar-padding)
          py-lg
        `,
        // When sidebar is collapsed, we calculate the padding based on the sidebar collapsed width minus the min-width of the menu items (to let the icon visible)
        `
          accepted-exception:group-data-[sidebar-expanded=false]:px-[calc(calc(var(--sidebar-width-collapsed)-var(--spacing-3xl))/2)]
        `
      )}
      data-testid="sidebar-user-controls"
    >
      <ul className="grid grid-cols-1 gap-xs">
        {appShellData.isAuthenticated && !appShellData.reauthenticate ? (
          <>
            <li>
              <Tooltip
                content={showTooltip ? 'Organisation Settings' : undefined}
                noTabIndex
              >
                {(targetProps) => (
                  <Link
                    to="/settings/profile"
                    isActive={pathname.startsWith('/settings')}
                    mode="navigation-item"
                    icon={SettingsIcon}
                    {...targetProps}
                  >
                    Settings
                  </Link>
                )}
              </Tooltip>
            </li>

            <li>
              <Tooltip
                content={showTooltip ? 'User Menu' : undefined}
                noTabIndex
              >
                {(targetProps) => (
                  <DropdownTrigger
                    renderTrigger={({ buttonProps }) => (
                      <Button
                        {...buttonProps}
                        {...targetProps}
                        mode="navigation-item"
                        icon={UserIcon}
                        aria-haspopup="menu"
                        data-testid="toggle-user-menu"
                      >
                        {appShellData.accounts.current ? (
                          <div className="grid gap-2xs">
                            {appShellData.accounts.current.organisationName && (
                              <span>
                                {appShellData.accounts.current.organisationName}
                              </span>
                            )}

                            <small
                              className={`
                                truncate text-foreground-sidebar-subdued
                              `}
                            >
                              {appShellData.accounts.current.email}
                            </small>
                          </div>
                        ) : (
                          'Account'
                        )}

                        <div
                          className={`
                            flex items-center
                            group-data-[sidebar-expanded=false]:hidden
                          `}
                        >
                          <EllipsisVerticalIcon size="sm" />
                        </div>
                      </Button>
                    )}
                    placement="right-end"
                  >
                    <UserNav appShellData={appShellData} />
                  </DropdownTrigger>
                )}
              </Tooltip>
            </li>
          </>
        ) : (
          <>
            <li>
              <Tooltip content={showTooltip ? 'Login' : undefined} noTabIndex>
                {(targetProps) => (
                  <Link
                    to={
                      pathname.startsWith('/login')
                        ? { pathname: '/login', search }
                        : {
                            pathname: '/login',
                            search: `redirectUrl=${encodeURIComponent(
                              location.href.replace('/angular', '/platform')
                            )}`,
                          }
                    }
                    mode="navigation-item"
                    data-testid="login-button"
                    icon={UserIcon}
                    {...targetProps}
                  >
                    Login
                  </Link>
                )}
              </Tooltip>
            </li>
            <li>
              <Tooltip
                content={showTooltip ? 'Register' : undefined}
                noTabIndex
              >
                {(targetProps) => (
                  <Link
                    to="/registration/wizard/start"
                    mode="navigation-item"
                    data-testid="register-button"
                    icon={IdCardIcon}
                    data-help-annotation="register-start:1"
                    {...targetProps}
                  >
                    Register
                  </Link>
                )}
              </Tooltip>
            </li>
          </>
        )}

        {developerTools && (
          <li>
            <Suspense
              fallback={
                <Link
                  mode="navigation-item"
                  icon={BugIcon}
                  to="/developer-tools"
                >
                  Developer Tools
                </Link>
              }
            >
              <DeveloperTools />
            </Suspense>
          </li>
        )}
      </ul>
    </div>
  );
};
